import { useEffect, useMemo, useState } from "react";

import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { HttpMainApi } from "../../interface/main-api";
import { userState } from "../../interface/MainInterface";
import LoadingCircle from "../../utils/LoadingCircle";

interface PropsType {
  userState: userState;
}

interface Month {
  value: string;
  label: string;
}

type ReservationType = "전체" | "취소" | "예약" | "확정";

interface BookCafeReservationRow {
  id: string;
  reservDate: string;
  reservDt: string;
  slot: string;
  useTime: string;
  userAddr: string;
  userName: string;
  reservationType: Exclude<ReservationType, "전체">;
}

const mainApi = new HttpMainApi();

function generateMonths() {
  const months: Month[] = [];
  const currentDate = new Date();
  for (let i = -1; i < 6; i += 1) {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
    const yearMonth = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, "0")}`;
    const formattedMonth = `${date.getFullYear()}년 ${String(date.getMonth() + 1).padStart(2, "0")}월`;
    months.push({ value: yearMonth, label: formattedMonth });
  }
  return months;
}

function formatDateLabel(date: string) {
  if (!date || date.length !== 8) return date || "-";
  return `${date.slice(0, 4)}년 ${date.slice(4, 6)}월 ${date.slice(6, 8)}일`;
}

function formatAddress(address = "") {
  const normalized = address.trim().replace(/\s+/g, " ");
  const match = normalized.match(/^(\d+)\s*동?\s+(\d+)\s*호?$/);
  if (!match) return normalized || "-";
  return `${match[1]}동 ${match[2].padStart(4, "0")}호`;
}

function addHour(slot: string) {
  if (!/^\d{2}:\d{2}$/.test(slot)) return "-";
  return `${String(Number(slot.slice(0, 2)) + 1).padStart(2, "0")}:${slot.slice(3, 5)}`;
}

function slotStartMs(reservDate: string, slot: string) {
  return new Date(
    `${reservDate.slice(0, 4)}-${reservDate.slice(4, 6)}-${reservDate.slice(6, 8)}T${slot}:00+09:00`,
  ).getTime();
}

function normalizeReservation(row: any, index: number, serverNowMs: number): BookCafeReservationRow {
  const reservDate = row.reservDate || String(row.sk || "").slice(0, 8);
  const slot = row.slot || row.useStartTime || String(row.sk || "").slice(8, 12).replace(/(\d{2})(\d{2})/, "$1:$2");
  const reservationType = row.reservState === "취소"
    ? "취소"
    : slotStartMs(reservDate, slot) <= serverNowMs
      ? "확정"
      : "예약";

  return {
    id: row.sk || `${row.reservationId || "no-reservation"}-${slot || index}`,
    reservDate,
    reservDt: row.reservDt || formatDateLabel(reservDate),
    slot,
    useTime: row.useTime || `${slot}~${row.useEndTime || addHour(slot)}`,
    userAddr: formatAddress(row.userAddr),
    userName: row.userName || "-",
    reservationType,
  };
}

function responseRows(response: any) {
  if (response?.code === "400") return [];
  if (response?.code === "200" && Array.isArray(response.response?.result)) {
    return response.response.result;
  }
  throw new Error(response?.response?.error_msg || "북카페 예약 현황을 조회하지 못했습니다.");
}

const BookCafeReservationStatus = (_props: PropsType) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectMonth, setSelectMonth] = useState("");
  const [months, setMonths] = useState<Month[]>([]);
  const [selectType, setSelectType] = useState<ReservationType>("전체");
  const [reservations, setReservations] = useState<BookCafeReservationRow[]>([]);

  useEffect(() => {
    const generatedMonths = generateMonths();
    setMonths(generatedMonths);
    if (generatedMonths.length > 0) setSelectMonth(generatedMonths[1].value);
  }, []);

  const getReservationList = async () => {
    setIsLoading(true);
    setReservations([]);
    try {
      const [activeResponse, cancelledResponse, policyResponse] = await Promise.all([
        mainApi.get_account_list({ pk: "BOOKCAFE", selectMonth, selectState: "신청" }),
        mainApi.get_account_list({ pk: "BOOKCAFE", selectMonth, selectState: "취소" }),
        mainApi.get_book_cafe_policy({}),
      ]);
      const serverNowMs = new Date(policyResponse?.response?.serverNow).getTime();
      if (policyResponse?.code !== "200" || Number.isNaN(serverNowMs)) {
        throw new Error(policyResponse?.response?.error_msg || "서버 기준 시각을 확인하지 못했습니다.");
      }
      const rows = [...responseRows(activeResponse), ...responseRows(cancelledResponse)]
        .map((row, index) => normalizeReservation(row, index, serverNowMs))
        .sort((left, right) => `${left.reservDate}${left.slot}`.localeCompare(`${right.reservDate}${right.slot}`));
      setReservations(rows);
    } catch (error) {
      setReservations([]);
      alert(error instanceof Error ? error.message : "북카페 예약 현황을 조회하지 못했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  const visibleReservations = useMemo(
    () => selectType === "전체"
      ? reservations
      : reservations.filter((reservation) => reservation.reservationType === selectType),
    [reservations, selectType],
  );

  const columns: GridColDef[] = [
    {
      field: "reservDt",
      headerName: "예약일",
      width: 170,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Typography component="span" sx={{ color: "#d32f2f", fontWeight: 700 }}>
          {params.row.reservDt}
        </Typography>
      ),
    },
    {
      field: "useTime",
      headerName: "이용 시간",
      width: 150,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "userAddr",
      headerName: "주소",
      width: 160,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "userName",
      headerName: "예약자",
      width: 130,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "reservationType",
      headerName: "구분",
      width: 100,
      align: "center",
      headerAlign: "center",
    },
  ];

  return (
    <>
      <Stack direction="column" spacing={2} sx={{ p: "32px", pb: "16px !important" }}>
        <Box className="sub-header-root">
          <Typography variant="h5" gutterBottom className="sub-header-title">
            북카페 예약 현황
          </Typography>
        </Box>

        <Stack direction="row" spacing={2} justifyContent="flex-end" alignItems="center" flexWrap="wrap">
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel id="book-cafe-reservation-status-month-label">기준</InputLabel>
            <Select
              labelId="book-cafe-reservation-status-month-label"
              value={selectMonth}
              label="기준"
              onChange={(event: SelectChangeEvent<string>) => setSelectMonth(event.target.value)}
              sx={{ height: 46 }}
            >
              {months.map((month) => (
                <MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel id="book-cafe-reservation-status-type-label">구분</InputLabel>
            <Select
              labelId="book-cafe-reservation-status-type-label"
              value={selectType}
              label="구분"
              onChange={(event: SelectChangeEvent<ReservationType>) => setSelectType(event.target.value as ReservationType)}
              sx={{ height: 46 }}
            >
              {(["전체", "취소", "예약", "확정"] as ReservationType[]).map((type) => (
                <MenuItem key={type} value={type}>{type}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            size="small"
            color="primary"
            aria-label="조회"
            onClick={() => void getReservationList()}
            sx={{ borderRadius: "4px", width: "54px", height: "54px" }}
          >
            <SearchIcon />
          </Button>
        </Stack>

        <Box sx={{ flexGrow: 1 }}>
          <Grid>
            <DataGrid
              rows={visibleReservations}
              columns={columns}
              disableRowSelectionOnClick
              style={{ height: 700 }}
              getRowId={(row) => row.id}
            />
            {!isLoading && visibleReservations.length === 0 && (
              <Typography color="text.secondary" sx={{ mt: 2, textAlign: "center" }}>
                조회된 북카페 예약이 없습니다.
              </Typography>
            )}
          </Grid>
        </Box>
      </Stack>
      <LoadingCircle loading={isLoading} />
    </>
  );
};

export default BookCafeReservationStatus;
